import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { psychologistService } from '../../../services/psychologistService';
import { useAuth } from '../../../contexts/AuthContext';

const ScheduleIntroduction = ({ onComplete, onBack, onSkip }) => {
  const [daysError, setDaysError] = useState(false);
  const { userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [scheduleSettings, setScheduleSettings] = useState({
    sessionDuration: '50',
    workingDays: [],
    startTime: '09:00',
    endTime: '18:00',
    lunchBreak: true,
    lunchStart: '12:00',
    lunchEnd: '13:00',
  });

  const sessionDurationOptions = [
    { value: '30', label: '30 minutos' },
    { value: '45', label: '45 minutos' },
    { value: '50', label: '50 minutos' },
    { value: '60', label: '60 minutos' },
    { value: '90', label: '90 minutos' },
  ];

  const weekDays = [
    { id: 'monday', label: 'Segunda' },
    { id: 'tuesday', label: 'Terça' },
    { id: 'wednesday', label: 'Quarta' },
    { id: 'thursday', label: 'Quinta' },
    { id: 'friday', label: 'Sexta' },
    { id: 'saturday', label: 'Sábado' },
    { id: 'sunday', label: 'Domingo' },
  ];

  const sampleAppointments = [
    {
      id: 1,
      time: '09:00',
      patient: 'Maria Silva',
      status: 'confirmed',
      duration: '50 min',
    },
    {
      id: 2,
      time: '10:00',
      patient: 'João Santos',
      status: 'pending',
      duration: '50 min',
    },
    {
      id: 3,
      time: '14:00',
      patient: 'Ana Costa',
      status: 'confirmed',
      duration: '50 min',
    },
    {
      id: 4,
      time: '15:00',
      patient: 'Disponível',
      status: 'available',
      duration: '50 min',
    },
  ];

  const features = [
    {
      icon: 'Calendar',
      title: 'Visualização Intuitiva',
      description: 'Veja todos os compromissos do dia em uma interface clara',
    },
    {
      icon: 'Clock',
      title: 'Horários Flexíveis',
      description: 'Configure duração de sessões e intervalos personalizados',
    },
    {
      icon: 'Bell',
      title: 'Lembretes Automáticos',
      description: 'WhatsApp automático para confirmações e lembretes',
    },
    {
      icon: 'Users',
      title: 'Gestão de Pacientes',
      description: 'Acesso rápido ao histórico durante o agendamento',
    },
  ];

  const handleDayToggle = (dayId) => {
    setScheduleSettings((prev) => ({
      ...prev,
      workingDays: prev?.workingDays?.includes(dayId)
        ? prev?.workingDays?.filter((d) => d !== dayId)
        : [...prev?.workingDays, dayId],
    }));
  };

  const handleComplete = async () => {

    // Validação
    if (scheduleSettings.workingDays.length === 0) {
      setDaysError(true);
      // Opcional: scroll suave até a seção de dias
      document.querySelector('[data-days-section]')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }

    setDaysError(false);    
    setIsSubmitting(true);
    
    try {
      // Save schedule settings to database
      const scheduleUpdates = {
        available_days: scheduleSettings?.workingDays,
        consultation_duration: parseInt(scheduleSettings?.sessionDuration),
        work_begin_time: scheduleSettings?.startTime,
        work_end_time: scheduleSettings?.endTime,
        work_lunch_interval: scheduleSettings?.lunchBreak
          ? {
              start: scheduleSettings?.lunchStart,
              end: scheduleSettings?.lunchEnd,
            }
          : null,
      };

      const { error: saveError } = await psychologistService?.updatePsychologist(
        userProfile?.id,
        scheduleUpdates
      );

      if (saveError) {
        console.error('Error saving schedule settings:', saveError);
        alert('Erro ao salvar configurações de horário. Por favor, tente novamente.');
        setIsSubmitting(false);
        return;
      }

      // Pass schedule settings to parent for completion
      onComplete(scheduleSettings);
    } catch (error) {
      console.error('Schedule save error:', error);
      alert('Erro ao salvar configurações. Por favor, tente novamente.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 border border-primary/20">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Icon name="Calendar" size={24} color="#FFFFFF" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Organize Sua Agenda Profissional
            </h3>
            <p className="text-muted-foreground">
              Configure seus horários de atendimento, duração das sessões e deixe o sistema gerenciar confirmações e lembretes automaticamente via WhatsApp.
            </p>
          </div>
        </div>
      </div>

        <div className="mt-4 p-4 bg-accent/10 rounded-lg border border-accent/20">
          <p className="text-sm text-foreground flex items-start gap-2">
            <Icon name="Info" size={16} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <span>
              Você poderá ajustar todas essas preferências a qualquer momento na configuração da sua conta.
            </span>
          </p>
        </div>


      {/*
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {features?.map((feature) => (
          <div
            key={feature?.title}
            className="bg-card rounded-lg p-4 border border-border hover:border-primary/50 transition-all duration-200"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name={feature?.icon} size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h4 className="font-medium text-foreground mb-1">
                  {feature?.title}
                </h4>
                <p className="text-sm text-muted-foreground">
                  {feature?.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
      */}
      
      <div className="bg-card rounded-xl p-6 border border-border">
        <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Settings" size={20} color="var(--color-primary)" />
          Configurações Básicas
        </h4>

        <div className="space-y-6">
          <Select
            label="Duração Padrão das Sessões"
            options={sessionDurationOptions}
            value={scheduleSettings?.sessionDuration}
            onChange={(value) =>
              setScheduleSettings((prev) => ({ ...prev, sessionDuration: value }))
            }
            description="Você poderá ajustar individualmente para cada paciente"
          />

          <div data-days-section>
            <label className="block text-sm font-medium text-foreground mb-3">
              Dias de Atendimento
            </label>

            {/* Mensagem de erro */}
            {daysError && (
              <p className="text-sm text-destructive mb-3 font-medium">
                Selecione pelo menos um dia da semana
              </p>
            )}

            <div 
              className={`grid grid-cols-2 sm:grid-cols-4 gap-2 p-1 rounded-lg transition-all ${
                daysError ? 'bg-destructive/10 border border-destructive/40' : ''
              }`}
            >
              
            {weekDays.map((day) => (
              <div
                key={day.id}
                onClick={() => {
                  handleDayToggle(day.id);
                  // Limpa erro ao selecionar qualquer dia
                  if (daysError) setDaysError(false);
                }}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all duration-200 text-center ${
                  scheduleSettings.workingDays.includes(day.id)
                    ? 'border-primary bg-primary/10 text-primary font-medium'
                    : 'border-border bg-muted/50 text-muted-foreground hover:border-primary/50 hover:bg-muted'
                } ${daysError && !scheduleSettings.workingDays.includes(day.id) ? 'border-destructive/40' : ''}`}
              >
                <p className="text-sm">{day.label}</p>
              </div>
            ))}
            
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Horário de Início
              </label>
              <input
                type="time"
                value={scheduleSettings?.startTime}
                onChange={(e) =>
                  setScheduleSettings((prev) => ({
                    ...prev,
                    startTime: e?.target?.value,
                  }))
                }
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Horário de Término
              </label>
              <input
                type="time"
                value={scheduleSettings?.endTime}
                onChange={(e) =>
                  setScheduleSettings((prev) => ({
                    ...prev,
                    endTime: e?.target?.value,
                  }))
                }
                className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          <Checkbox
            label="Incluir intervalo para almoço"
            checked={scheduleSettings?.lunchBreak}
            onChange={(e) =>
              setScheduleSettings((prev) => ({
                ...prev,
                lunchBreak: e?.target?.checked,
              }))
            }
          />

          {scheduleSettings?.lunchBreak && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pl-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Início do Almoço
                </label>
                <input
                  type="time"
                  value={scheduleSettings?.lunchStart}
                  onChange={(e) =>
                    setScheduleSettings((prev) => ({
                      ...prev,
                      lunchStart: e?.target?.value,
                    }))
                  }
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Fim do Almoço
                </label>
                <input
                  type="time"
                  value={scheduleSettings?.lunchEnd}
                  onChange={(e) =>
                    setScheduleSettings((prev) => ({
                      ...prev,
                      lunchEnd: e?.target?.value,
                    }))
                  }
                  className="w-full px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-xl p-6 border border-border">
        <h4 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Eye" size={20} color="var(--color-primary)" />
          Exemplo de Agenda Diária
        </h4>

        <div className="space-y-2">
          {sampleAppointments?.map((appointment) => (
            <div
              key={appointment?.id}
              className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                appointment?.status === 'available' ?'border-dashed border-border bg-muted/30' :'border-border bg-card hover:border-primary/50'
              } transition-all duration-200`}
            >
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-lg font-semibold text-foreground">
                    {appointment?.time}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {appointment?.duration}
                  </p>
                </div>
                <div className="h-10 w-px bg-border" />
                <div>
                  <p className="font-medium text-foreground">
                    {appointment?.patient}
                  </p>
                  {appointment?.status !== 'available' && (
                    <span
                      className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        appointment?.status === 'confirmed' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                      }`}
                    >
                      <Icon
                        name={
                          appointment?.status === 'confirmed' ?'CheckCircle' :'Clock'
                        }
                        size={12}
                      />
                      {appointment?.status === 'confirmed' ?'Confirmado' :'Pendente'}
                    </span>
                  )}
                </div>
              </div>
              {appointment?.status === 'available' && (
                <Button variant="outline" size="sm" iconName="Plus">
                  Agendar
                </Button>
              )}
            </div>
          ))}
        </div>

      </div>
      <div className="flex items-center justify-between pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          iconName="ChevronLeft"
          iconPosition="left"
          disabled={isSubmitting}
        >
          Voltar
        </Button>
        <div className="flex items-center gap-2">
          <Button 
            type="button" 
            variant="ghost" 
            onClick={onSkip}
            disabled={isSubmitting}
          >
            Pular
          </Button>
          <Button
            type="button"
            variant="default"
            onClick={handleComplete}
            iconName="Check"
            iconPosition="right"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : 'Concluir Configuração'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleIntroduction;